import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import styles from "./AgentPlanList.module.css";
import AdminHeader from "../Header/AdminHeader";
import AdminLeftMenu from "../Navbar/AdminLeftMenu";
import { NODE_HOST_URL } from "../Dashboard/api/Api";
import axios from "../../axios/Axios";
import { userloginDetails } from "../Utils/AdminAuth";

const AgentBillDetails = () => {
    const history = useHistory();
    const location = useLocation();
    const { userId: clientId } = useParams();

    const [activeSection, setActiveSection] = useState("total");
    const [totalBills, setTotalBills] = useState([]);
    const [pendingBills, setPendingBills] = useState([]);
    const [billPagination, setBillPagination] = useState({ page: 1, limit: 10, total: 0, total_pages: 1 });
    const [pendingPagination, setPendingPagination] = useState({ page: 1, limit: 10, total: 0, total_pages: 1 });
    const [pendingTotal, setPendingTotal] = useState(0);
    const [isTotalServerPaginated, setIsTotalServerPaginated] = useState(true);
    const [isPendingServerPaginated, setIsPendingServerPaginated] = useState(true);
    const [billPage, setBillPage] = useState(1);
    const [pendingPage, setPendingPage] = useState(1);
    const [billLimit] = useState(10);
    const [isBillsLoading, setIsBillsLoading] = useState(false);
    const [activePlan, setActivePlan] = useState(null);
    const [isActivePlanLoading, setIsActivePlanLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("cash");
    const [isPayModalOpen, setIsPayModalOpen] = useState(false);
    const [payTarget, setPayTarget] = useState(null);
    const [pinValue, setPinValue] = useState("");
    const [transactionId, setTransactionId] = useState("");

    const selectedUser = location.state?.user || null;

    const lStorage = userloginDetails();
    const adminUserId = lStorage?.user?.userId;
    const userToken = lStorage?.token;

    const fetchClientBills = async (clientId, page = 1) => {
        if (!clientId) return;
        try {
            setIsBillsLoading(true);
            const payload = {
                "user_id": adminUserId,
                "token": userToken,
                "method": "client_bills",
                "client_id": clientId,
                "page": page,
                "limit": billLimit
            };
            const response = await axios.post(`${NODE_HOST_URL}/agent_management`, payload);
            const result = response.data;
            if (result.success) {
                const bills = result.data || [];
                setTotalBills(bills);
                if (Number.isFinite(Number(result.pending_total))) {
                    setPendingTotal(Number(result.pending_total));
                }
                if (result.pagination) {
                    setIsTotalServerPaginated(true);
                    setBillPagination(result.pagination);
                } else {
                    setIsTotalServerPaginated(false);
                    setBillPagination({
                        page,
                        limit: billLimit,
                        total: bills.length,
                        total_pages: Math.max(1, Math.ceil(bills.length / billLimit))
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching client bills:", error);
        } finally {
            setIsBillsLoading(false);
        }
    };

    const fetchPendingBills = async (clientId, page = 1) => {
        if (!clientId) return;
        try {
            setIsBillsLoading(true);
            const payload = {
                "user_id": adminUserId,
                "token": userToken,
                "method": "client_bills_pending",
                "client_id": clientId,
                "page": page,
                "limit": billLimit
            };
            const response = await axios.post(`${NODE_HOST_URL}/agent_management`, payload);
            const result = response.data;
            if (result.success) {
                const bills = result.data || [];
                setPendingBills(bills);
                setPendingTotal(Number(result.pending_total || bills.length || 0));
                if (result.pagination) {
                    setIsPendingServerPaginated(true);
                    setPendingPagination(result.pagination);
                } else {
                    setIsPendingServerPaginated(false);
                    setPendingPagination({
                        page,
                        limit: billLimit,
                        total: bills.length,
                        total_pages: Math.max(1, Math.ceil(bills.length / billLimit))
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching pending bills:", error);
        } finally {
            setIsBillsLoading(false);
        }
    };

    const fetchActivePlan = async (clientId) => {
        if (!clientId) return;
        try {
            setIsActivePlanLoading(true);
            const payload = {
                "user_id": adminUserId,
                "token": userToken,
                "client_id": clientId,
                "method": "agent_plan_retrieve"
            };
            const response = await axios.post(`${NODE_HOST_URL}/user`, payload);
            const result = response.data;
            if (result.success && result.data?.client) {
                setActivePlan(result.data.client);
            } else {
                setActivePlan(null);
            }
        } catch (error) {
            console.error("Error fetching active plan:", error);
            setActivePlan(null);
        } finally {
            setIsActivePlanLoading(false);
        }
    };
    const handlePayBill = (bill) => {
        setPayTarget(bill);
        setPinValue("");
        setPaymentMethod("cash");
        setTransactionId("");
        setIsPayModalOpen(true);
    };

    const closePayModal = () => {
        setIsPayModalOpen(false);
        setPayTarget(null);
        setPinValue("");
        setPaymentMethod("cash");
        setTransactionId("");
    };

    const confirmPayBill = async () => {
        if (!pinValue.trim() || !payTarget) return;

        const billId = payTarget?._id || payTarget?.bill_id || payTarget?.user_id;
        try {
            const payload = {
                "user_id": adminUserId,
                "token": userToken,
                "method": "pay_bill",
                "bill_id": billId,
                "payment_method": paymentMethod,
                "pin": Number(pinValue),
                "transaction_id": transactionId.trim() ? transactionId.trim() : ""
            };
            const response = await axios.post(`${NODE_HOST_URL}/agent_management`, payload);
            const result = response.data;
            if (result.success) {
                fetchClientBills(clientId, billPage);
                fetchPendingBills(clientId, pendingPage);
                closePayModal();
            }
        } catch (error) {
            console.error("Error paying bill:", error);
        }
    };

    useEffect(() => {
        if (!clientId || activeSection !== "total") return;
        fetchClientBills(clientId, billPage);
    }, [activeSection, billPage, clientId]);

    useEffect(() => {
        if (!clientId || activeSection !== "pending") return;
        fetchPendingBills(clientId, pendingPage);
    }, [activeSection, pendingPage, clientId]);

    useEffect(() => {
        if (!clientId || activeSection !== "active") return;
        fetchActivePlan(clientId);
    }, [activeSection, clientId]);

    const displayName = selectedUser?.username || selectedUser?.name || selectedUser?.user_name || "";

    const totalPageBills = isTotalServerPaginated
        ? totalBills
        : totalBills.slice((billPage - 1) * billLimit, billPage * billLimit);

    const pendingPageBills = isPendingServerPaginated
        ? pendingBills
        : pendingBills.slice((pendingPage - 1) * billLimit, pendingPage * billLimit);

    const totalTabCount = billPagination.total || totalBills.length;
    const pendingTabCount = Number.isFinite(Number(pendingTotal))
        ? Number(pendingTotal)
        : (pendingPagination.total || pendingBills.length);

    const activePlanName = activePlan?.plan_name || activePlan?.plan?.plan_name || "-";
    const activePlanBilling = activePlan?.billing_type || activePlan?.plan?.billing_type || "-";
    const activePlanQty = Number(activePlan?.agent_quantity ?? activePlan?.plan?.agent_quantity ?? 0);
    const activePlanPrice = Number(activePlan?.agent_price ?? activePlan?.plan?.agent_price ?? 0);
    const activePlanDiscount = Number(activePlan?.agent_discount ?? activePlan?.plan?.agent_discount ?? 0);
    const activePlanUsages = Number(activePlan?.agent_usages ?? activePlan?.plan?.agent_usages ?? 0);
    const activePlanTotal = Number(activePlan?.total_price ?? activePlan?.plan?.total_price ?? 0);
    const activePlanStatus = Number(activePlan?.status ?? activePlan?.plan?.status ?? 0);
    const activePlanGrace = Number(activePlan?.grace_period ?? activePlan?.plan?.grace_period ?? 0);
    const activePlanExpiryRaw = activePlan?.plan_expiry || activePlan?.plan?.plan_expiry || null;
    const activePlanDiscounted = activePlanDiscount
        ? activePlanPrice - (activePlanPrice * activePlanDiscount) / 100
        : activePlanPrice;

    const formatDate = (value) => {
        if (!value) return "-";
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return "-";
        return parsed.toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "2-digit"
        });
    };

    const sectionTabs = [
        { key: "total", label: "Total Bills", count: totalTabCount },
        { key: "pending", label: "Pending Bills", count: pendingTabCount },
        { key: "active", label: "Active Plan"},
    ];

    return (
        <div className="wrapper">
            <AdminLeftMenu />
            <div className="main">
                <AdminHeader />
                <div className="container-fluid p-3 p-md-4">
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mt-3 mb-4 gap-2">
                        <div>
                            <h5 className={styles.pageTitle}>Agent Bills</h5>
                            <div className={styles.subtitle}>
                                {displayName ? `${displayName} (User ID: ${clientId})` : `User ID: ${clientId}`}
                            </div>
                        </div>
                        {/* <button className="btn btn-outline-secondary" onClick={() => history.push("/admin/agent-bill")}>
                            Back to Users
                        </button> */}
                    </div>

                    {/* Section header buttons */}
                    <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: 0 }}>
                        {sectionTabs.map((sec) => (
                            <button
                                key={sec.key}
                                onClick={() => setActiveSection(sec.key)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    borderBottom: activeSection === sec.key ? "2.5px solid #4f46e5" : "2.5px solid transparent",
                                    color: activeSection === sec.key ? "#4f46e5" : "#64748b",
                                    fontWeight: activeSection === sec.key ? 700 : 500,
                                    fontSize: 14,
                                    padding: "10px 24px",
                                    cursor: "pointer",
                                    marginBottom: -2,
                                    transition: "color 0.15s, border-color 0.15s",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                }}
                            >
                                {sec.label}
                                <span style={{
                                    background: activeSection === sec.key ? "#eef2ff" : "#f1f5f9",
                                    color: activeSection === sec.key ? "#4f46e5" : "#64748b",
                                    borderRadius: 20,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    padding: "1px 9px",
                                    minWidth: 24,
                                    textAlign: "center",
                                }}>
                                    {sec.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Section content */}
                    <div className={styles.tableWrapper} style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: "none" }}>
                        <div className="p-3">
                            {activeSection !== "active" && (
                                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between text-muted small mb-2">
                                    <div>
                                        {activeSection === "pending" ? (
                                            <span style={{ color: "#0f172a", fontWeight: 700 }}>
                                                Page {pendingPagination.page} of {pendingPagination.total_pages} &nbsp;·&nbsp; Total {pendingPagination.total}
                                            </span>
                                        ) : (
                                            <span>
                                                Page {billPagination.page} of {billPagination.total_pages} &nbsp;·&nbsp; Total {billPagination.total}
                                            </span>
                                        )}
                                    </div>
                                    {activeSection === "pending" && (
                                        <div>
                                            Pending total: <span style={{ fontWeight: 700, color: "#0f172a" }}>{pendingTotal}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {activeSection !== "active" && isBillsLoading && (
                                <div className="text-muted small mb-2">Loading bills...</div>
                            )}
                            {activeSection === "active" && isActivePlanLoading && (
                                <div className="text-muted small mb-2">Loading plan...</div>
                            )}
                            <div className="table-responsive">
                                {activeSection === "total" && (
                                    <table className="table table-hover align-middle mb-0">
                                        <thead style={{ background: "#f8fafc" }}>
                                            <tr>
                                                <th>Bill Id</th>
                                                <th>Plan</th>
                                                <th>Paid Amount</th>
                                                <th>Outstanding</th>
                                                <th>Status</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {totalBills.length === 0 && (
                                                <tr>
                                                    <td colSpan={6} className="text-center text-muted py-4">
                                                        {isBillsLoading ? "Loading bills..." : "No bills."}
                                                    </td>
                                                </tr>
                                            )}
                                            {totalPageBills.map((bill) => (
                                                <tr key={bill._id || bill.transaction_id || bill.createdAt}>
                                                    <td>{bill._id || bill.transaction_id || "-"}</td>
                                                    <td>{bill.plan?.plan_name || "-"}</td>
                                                    <td>{bill.paid_amount}</td>
                                                    <td>{bill.outstanding_amount}</td>
                                                    <td className={bill.status === "paid" ? "text-success fw-semibold" : "text-warning fw-semibold"}>
                                                        {bill.status || "-"}
                                                    </td>
                                                    {/* <td className="text-end">
                                                        {bill.status === "pending" ? (
                                                            <button
                                                                className={styles.actionBtn}
                                                                onClick={() => handlePayBill(bill)}
                                                            >
                                                                Pay
                                                            </button>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td> */}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}

                                {activeSection === "pending" && (
                                    <table className="table table-hover align-middle mb-0">
                                        <thead style={{ background: "#f8fafc" }}>
                                            <tr>
                                                <th>Bill Id</th>
                                                <th>Plan</th>
                                                <th>Paid Amount</th>
                                                <th>Outstanding</th>
                                                <th>Status</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pendingBills.length === 0 && (
                                                <tr>
                                                    <td colSpan={6} className="text-center text-muted py-4">
                                                        {isBillsLoading ? "Loading bills..." : "No pending bills."}
                                                    </td>
                                                </tr>
                                            )}
                                            {pendingPageBills.map((bill) => (
                                                <tr key={bill._id || bill.transaction_id || bill.createdAt}>
                                                    <td>{bill._id || bill.transaction_id || "-"}</td>
                                                    <td>{bill.plan?.plan_name || "-"}</td>
                                                    <td>{bill.paid_amount}</td>
                                                    <td>{bill.outstanding_amount}</td>
                                                    <td className="text-warning fw-semibold">Pending</td>
                                                    <td className="text-end">
                                                        <button
                                                            className={styles.actionBtn}
                                                            onClick={() => handlePayBill(bill)}
                                                        >
                                                            Pay
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}

                                {activeSection === "active" && (
                                    <div className={styles.activePlanCard}>
                                        {!activePlan && (
                                            <div className="text-center text-muted py-4">
                                                {isActivePlanLoading ? "Loading plan..." : "No active plan."}
                                            </div>
                                        )}
                                        {activePlan && (
                                            <div>
                                                <div className={styles.activePlanHeader}>
                                                    <div className={styles.activePlanTitle}>{activePlanName}</div>
                                                    <div className={styles.activePlanBadge}>Active Plan</div>
                                                </div>
                                                <div className={styles.activePlanBody}>
                                                    <div className={styles.activePlanLeft}>
                                                        <div className={styles.activePlanChips}>
                                                            <span className={styles.activePlanChip}>{activePlanBilling}</span>
                                                            <span className={styles.activePlanChip}>{activePlanQty} agents</span>
                                                            {activePlanDiscount > 0 && (
                                                                <span className={styles.activePlanChip}>
                                                                    {activePlanDiscount}% discount
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className={styles.activePlanRight}>
                                                        <div className={styles.activePlanAgents}>
                                                            {activePlanQty} active agents
                                                        </div>
                                                        <div className={styles.activePlanPrice}>
                                                            {activePlanDiscount > 0 && (
                                                                <span className={styles.activePlanPriceOld}>
                                                                    ₹{activePlanPrice.toFixed(2)}/agent
                                                                </span>
                                                            )}
                                                            <span className={styles.activePlanPriceNew}>
                                                                ₹{activePlanDiscounted.toFixed(2)}/agent
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className={styles.activePlanGrid}>
                                                    <div className={styles.activePlanTile}>
                                                        <div className={styles.activePlanTileValue}>{activePlanPrice.toFixed(2)}</div>
                                                        <div className={styles.activePlanTileLabel}>Agent Price</div>
                                                    </div>
                                                    <div className={styles.activePlanTile}>
                                                        <div className={styles.activePlanTileValue}>{activePlanDiscount}%</div>
                                                        <div className={styles.activePlanTileLabel}>Agent Discount</div>
                                                    </div>
                                                    <div className={styles.activePlanTile}>
                                                        <div className={styles.activePlanTileValue}>{activePlanUsages}</div>
                                                        <div className={styles.activePlanTileLabel}>Agent Usages</div>
                                                    </div>
                                                    <div className={styles.activePlanTile}>
                                                        <div className={styles.activePlanTileValue}>{activePlanTotal.toFixed(2)}</div>
                                                        <div className={styles.activePlanTileLabel}>Total Price</div>
                                                    </div>
                                                    <div className={styles.activePlanTile}>
                                                        <div className={styles.activePlanTileValue}>{formatDate(activePlanExpiryRaw)}</div>
                                                        <div className={styles.activePlanTileLabel}>Plan Expiry</div>
                                                    </div>
                                                    <div className={styles.activePlanTile}>
                                                        <div className={styles.activePlanTileValue}>{activePlanGrace} days</div>
                                                        <div className={styles.activePlanTileLabel}>Grace Period</div>
                                                    </div>
                                                </div>
                                                <div className={styles.activePlanFooter}>
                                                    <div className={styles.activePlanFooterItem}>
                                                        Auto Renew: <span className={styles.activePlanFooterValue}>{activePlan?.auto_renew ? "Yes" : "No"}</span>
                                                    </div>
                                                    <div className={styles.activePlanFooterItem}>
                                                        Status: <span className={styles.activePlanFooterValue}>{activePlanStatus === 1 ? "Active" : "Inactive"}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {activeSection !== "active" && (
                                <div className={styles.detailPagination} style={{ marginTop: 12 }}>
                                    <div className={styles.detailPagInfo}>
                                        {activeSection === "pending" ? (
                                            <span style={{ color: "#0f172a", fontWeight: 700 }}>
                                                Page {pendingPagination.page} of {pendingPagination.total_pages}
                                            </span>
                                        ) : (
                                            <span>
                                                Page {billPagination.page} of {billPagination.total_pages}
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.detailPagButtons}>
                                        <button
                                            className="btn btn-outline-secondary btn-sm text-dark"
                                            onClick={() => {
                                                if (activeSection === "pending") {
                                                    setPendingPage((prev) => Math.max(1, prev - 1));
                                                    return;
                                                }
                                                setBillPage((prev) => Math.max(1, prev - 1));
                                            }}
                                            disabled={activeSection === "pending" ? pendingPage <= 1 : billPage <= 1}
                                        >
                                            Previous
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm text-dark"
                                            onClick={() => {
                                                if (activeSection === "pending") {
                                                    setPendingPage((prev) => Math.min(pendingPagination.total_pages, prev + 1));
                                                    return;
                                                }
                                                setBillPage((prev) => Math.min(billPagination.total_pages, prev + 1));
                                            }}
                                            disabled={activeSection === "pending" ? pendingPage >= pendingPagination.total_pages : billPage >= billPagination.total_pages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {isPayModalOpen && (
                        <>
                            <div
                                className="modal fade show"
                                style={{ display: "block" }}
                                role="dialog"
                                aria-modal="true"
                            >
                                <div className="modal-dialog modal-dialog-centered">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title">Pay Bill</h5>
                                            <button
                                                type="button"
                                                onClick={closePayModal}
                                                aria-label="Close"
                                                className="btn btn-light"
                                                style={{ width: 36, height: 36, padding: 0, borderRadius: 8 }}
                                            >
                                                <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>
                                                    ×
                                                </span>
                                            </button>
                                        </div>
                                        <div className="modal-body">
                                            <label className={styles.formLabel}>Payment Method</label>
                                            <select
                                                className="form-select mb-3"
                                                value={paymentMethod}
                                                onChange={(event) => setPaymentMethod(event.target.value)}
                                            >
                                                <option value="bank account">Bank Account</option>
                                                <option value="cash">Cash</option>
                                                <option value="cheque">Cheque</option>
                                            </select>
                                            <label className={styles.formLabel}>Transaction Id (optional)</label>
                                            <input
                                                type="text"
                                                className="form-control mb-3"
                                                placeholder="Transaction Id"
                                                value={transactionId}
                                                onChange={(event) => setTransactionId(event.target.value)}
                                            />
                                            <label className={styles.formLabel}>Enter PIN</label>
                                            <input
                                                type="password"
                                                className="form-control"
                                                placeholder="PIN"
                                                value={pinValue}
                                                onChange={(event) => setPinValue(event.target.value)}
                                            />
                                        </div>
                                        <div className="modal-footer">
                                            <button type="button" className="btn btn-outline-danger" onClick={closePayModal}>
                                                Cancel
                                            </button>
                                            <button type="button" className="btn btn-primary" onClick={confirmPayBill}>
                                                Pay Now
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                className="modal-backdrop fade show"
                                onClick={closePayModal}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AgentBillDetails;
